import apiClient from "./api";

export interface VendorPromotion {
  vendorId: string;
  promotionType: string;
  displayOrder: number;
  rating?: number;
  vendorCreatedAt: number;
  promotionTitle?: string;
  promotionDescription?: string;
  discountPercentage?: number;
  discountAmount?: number;
  validFrom?: number;
  validUntil?: number;
  isActive?: boolean;
  // Enriched vendor data (from backend join)
  vendorName?: string;
  vendorAvatar?: string;
  vendorEmail?: string;
  vendorCategory?: string;
  vendorServices?: string;
}

export interface GetPromotedVendorsResponse {
  success: boolean;
  data: {
    promotions: VendorPromotion[];
    count: number;
  };
  message: string;
}

export interface AddVendorToPromotionRequest {
  vendorId: string;
  displayOrder?: number;
  promotionTitle?: string;
  promotionDescription?: string;
  discountPercentage?: number;
  discountAmount?: number;
  validFrom?: number;
  validUntil?: number;
}

export interface ReorderVendorsRequest {
  vendorIds: string[];
}

export const PromotionsService = {
  // Get all promoted vendors
  getPromotedVendors: async (
    sortBy: 'displayOrder' | 'rating' | 'vendorCreatedAt' = 'displayOrder',
    ascending: boolean = true
  ): Promise<VendorPromotion[]> => {
    try {
      const response = await apiClient.get<GetPromotedVendorsResponse>(
        `/promotions/vendors`,
        {
          params: { sortBy, ascending }
        }
      );
      
      console.log('📊 Promoted Vendors Response:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data.promotions;
      }
      
      return [];
    } catch (error: any) {
      console.error('Error fetching promoted vendors:', error);
      throw error;
    }
  },

  // Search active vendors to add to promotions
  searchActiveVendors: async (
    searchQuery: string = '',
    page: number = 1,
    limit: number = 20
  ): Promise<{ vendors: any[]; totalCount: number; currentPage: number; totalPages: number }> => {
    try {
      const response = await apiClient.post(
        `/vendors/list`,
        {
          filters: {
            status: 'Active' // Must match VendorStatus.ACTIVE enum value
          },
          sort: {
            by: 'name',
            ascending: true
          },
          limit,
          page,
          pageBased: true
        }
      );
      
      console.log('🔍 Active Vendors Search Response:', response.data);
      
      const responseData = response.data;
      
      // Handle different response structures
      let vendors: any[] = [];
      let totalCount = 0;
      let currentPage = page;
      let totalPages = 1;
      
      if (responseData.data) {
        vendors = responseData.data.vendors || [];
        totalCount = responseData.data.totalCount || 0;
        currentPage = responseData.data.currentPage || page;
        totalPages = responseData.data.totalPages || 1;
      } else if (responseData.vendors) {
        vendors = responseData.vendors;
        totalCount = responseData.totalCount || 0;
        currentPage = responseData.currentPage || page;
        totalPages = responseData.totalPages || 1;
      }
      
      // Client-side filtering by search query if provided
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        vendors = vendors.filter((vendor: any) => 
          vendor.companyName?.toLowerCase().includes(query) ||
          vendor.contactName?.toLowerCase().includes(query) ||
          vendor.email?.toLowerCase().includes(query) ||
          vendor.vendorId?.toLowerCase().includes(query)
        );
      }
      
      return {
        vendors,
        totalCount,
        currentPage,
        totalPages
      };
    } catch (error: any) {
      console.error('Error searching active vendors:', error);
      throw error;
    }
  },

  // Add vendor to promotions
  addVendorToPromotion: async (request: AddVendorToPromotionRequest): Promise<VendorPromotion> => {
    try {
      const response = await apiClient.post<{ success: boolean; data: { promotion: VendorPromotion }; message: string }>(
        `/promotions/vendors`,
        request
      );
      
      console.log('✅ Vendor added to promotions:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data.promotion;
      }
      
      throw new Error('Failed to add vendor to promotions');
    } catch (error: any) {
      console.error('Error adding vendor to promotions:', error);
      
      // Extract more detailed error message
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to add vendor to promotions';
      
      // Create a new error with the detailed message
      const detailedError = new Error(errorMessage);
      (detailedError as any).response = error.response;
      throw detailedError;
    }
  },

  // Remove vendor from promotions
  removeVendorFromPromotion: async (vendorId: string): Promise<void> => {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `/promotions/vendors/${vendorId}`
      );
      
      console.log('🗑️ Vendor removed from promotions:', response.data);
      
      if (!response.data.success) {
        throw new Error('Failed to remove vendor from promotions');
      }
    } catch (error: any) {
      console.error('Error removing vendor from promotions:', error);
      throw error;
    }
  },

  // Reorder vendors
  reorderVendors: async (vendorIds: string[]): Promise<void> => {
    try {
      const response = await apiClient.put<{ success: boolean; message: string }>(
        `/promotions/vendors/reorder`,
        { vendorIds }
      );
      
      console.log('🔄 Vendors reordered:', response.data);
      
      if (!response.data.success) {
        throw new Error('Failed to reorder vendors');
      }
    } catch (error: any) {
      console.error('Error reordering vendors:', error);
      throw error;
    }
  },
};

export default PromotionsService;
